import { isTpoFinanceSelection } from "@logic/lib/constants"
import { ADDER_CATEGORY, UNIT_OF_MEASURE } from "@logic/lib/powur-os"
import { useRecoilValue } from "recoil"

import { NoneSelected } from "~/components/Icons/adderIcons"
import type { IPart } from "~/containers/Proposal/Setup/Form/setup-steps-types"
import { useDesignContext } from "~/containers/Proposal/state/designs/design-context"
import { homeownerRebateValidState } from "~/containers/Proposal/state/designs/design-state"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"

import { Category } from "./Category"
import Rebates from "./Rebates"
import { Adders, Card, CardsContainer, Container, StyledForm } from "./styles"
import Warranties from "./Warranties"
import { selectedCategoryState, useAdders } from "../../../../state/adders"
import { SetupMenuFormFooter } from "../../SetupMenuFooter"

const AddersSelection: IPart[`form`] = () => {
  const design = useDesignContext()
  const proposal = useProposalContext()
  const homeownerRebateValid = useRecoilValue(homeownerRebateValidState)
  const selectedCategory = useRecoilValue(selectedCategoryState)
  const {
    addersForCategory,
    toggleAdderSelected,
    setAdderCustomQuantity,
    setAdderPrice,
  } = useAdders()

  const selectedLoanProductIsPPA = isTpoFinanceSelection({
    financeType: proposal.local.financeType,
    loanType: proposal.local.selectedLoanProduct?.loanType,
  })

  return (
    <StyledForm id="addersSelection">
      <Container>
        <Category />
        <Adders>
          {selectedCategory === ADDER_CATEGORY.Rebates ? (
            <Rebates disableCards={proposal.isSaving || design.isSaving} />
          ) : selectedCategory === ADDER_CATEGORY.Warranties ? (
            <Warranties
              disableCards={
                selectedLoanProductIsPPA || proposal.isSaving || design.isSaving
              }
            />
          ) : (
            <CardsContainer data-testid="adders-cards-container">
              {addersForCategory.length ? (
                addersForCategory.map((proposalAdder) => {
                  const {
                    adder,
                    selected,
                    customPrice,
                    customQuantity,
                    cost,
                    isVariable,
                  } = proposalAdder

                  const hasQuantityInput =
                    !isVariable &&
                    (adder.unit === UNIT_OF_MEASURE.FOOT ||
                      adder.unit === UNIT_OF_MEASURE.SQFT ||
                      adder.unit === UNIT_OF_MEASURE.PROJECT ||
                      adder.unit === UNIT_OF_MEASURE.UNIT)

                  return (
                    <Card
                      key={adder.id}
                      id={adder.id}
                      testData={adder.powurOsId ?? adder.id}
                      selected={!!selected}
                      cost={cost}
                      title={adder.title}
                      toggle={toggleAdderSelected(adder.id, design.local)}
                      unit={adder.unit}
                      isVariableInput={isVariable}
                      value={
                        hasQuantityInput
                          ? customQuantity
                            ? Math.round(customQuantity)
                            : undefined
                          : isVariable
                            ? (customPrice ?? undefined)
                            : undefined
                      }
                      onChange={
                        hasQuantityInput
                          ? setAdderCustomQuantity(adder.id)
                          : isVariable
                            ? setAdderPrice(adder.id)
                            : undefined
                      }
                      disabled={proposal.isSaving || design.isSaving}
                    />
                  )
                })
              ) : (
                <div className="no-adders">
                  <NoneSelected />
                  <p>No Adders Available</p>
                </div>
              )}
            </CardsContainer>
          )}
        </Adders>
      </Container>
      <SetupMenuFormFooter
        entitiesToUpdate={[design, proposal]}
        shouldCloseOnSave={false}
        isLoading={proposal.isSaving || design.isSaving || !homeownerRebateValid}
      />
    </StyledForm>
  )
}

export default AddersSelection
